import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Download } from "lucide-react";
import axios from "axios";
import { API, useRequestModal, resolveImageUrl } from "../App";

const CollectionsPage = () => {
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingCatalog, setDownloadingCatalog] = useState(false);
  const { openModal } = useRequestModal();

  const handleDownloadCatalog = async () => {
    setDownloadingCatalog(true);
    try {
      const response = await axios.get(`${API}/catalog/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'INTROZZI_Catalog.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading catalog:", error);
    } finally {
      setDownloadingCatalog(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [collectionsRes, productsRes] = await Promise.all([
          axios.get(`${API}/collections`),
          axios.get(`${API}/products`)
        ]);
        setCollections(collectionsRes.data);
        setProducts(productsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Group products by collection
  const productsByCollection = collections.map(col => ({
    name: col.name,
    cover_image: col.cover_image,
    product_count: col.product_count,
    products: products.filter(p => p.collection === col.name)
  }));

  return (
    <div className="min-h-screen bg-black pt-24" data-testid="collections-page">
      {/* Page Header */}
      <section className="px-6 sm:px-12 lg:px-24 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">
                Browse by
              </p>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight">
                Collections
              </h1>
              <p className="text-gray-400 text-base sm:text-lg font-light mt-6 max-w-2xl">
                Explore our curated collections, each representing a distinct vision 
                of craftsmanship and bespoke design.
              </p>
            </div>
            <button
              onClick={handleDownloadCatalog}
              disabled={downloadingCatalog}
              className="inline-flex items-center gap-3 border border-white/40 rounded-full text-white px-6 py-3 text-sm tracking-widest uppercase font-medium hover:border-white hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50 whitespace-nowrap"
              data-testid="download-catalog-btn"
            >
              <Download size={16} strokeWidth={1.5} />
              {downloadingCatalog ? "Generating..." : "Download Catalog"}
            </button>
          </div>
        </motion.div>
      </section>

      {/* Collections */}
      <section className="px-6 sm:px-12 lg:px-24 pb-24 sm:pb-32">
        {loading ? (
          <div className="space-y-24">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-6 bg-[#121212] w-48 mb-8" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[1, 2, 3].map((j) => (
                    <div key={j}>
                      <div className="aspect-[4/5] bg-[#121212]" />
                      <div className="mt-4 h-4 bg-[#121212] w-2/3" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : collections.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-gray-500">No collections available.</p>
          </div>
        ) : (
          <div className="space-y-24">
            {productsByCollection.map((collection, collectionIndex) => (
              <motion.div
                key={collection.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: collectionIndex * 0.1 }}
              >
              {/* Collection Header */}
                <div className="mb-12">
                  {collection.cover_image && (
                    <div className="relative aspect-[21/9] overflow-hidden mb-8">
                      <img 
                        src={resolveImageUrl(collection.cover_image)} 
                        alt={collection.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute bottom-6 left-6 sm:left-8">
                        <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-2">
                          {collection.products.length} {collection.products.length === 1 ? 'Product' : 'Products'}
                        </p>
                        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl font-light text-white">
                          {collection.name}
                        </h2>
                      </div>
                    </div>
                  )}
                  {!collection.cover_image && (
                    <div className="flex items-end justify-between border-b border-white/10 pb-6 mb-0">
                      <div>
                        <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-2">
                          {collection.products.length} {collection.products.length === 1 ? 'Product' : 'Products'}
                        </p>
                        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-light text-white">
                          {collection.name}
                        </h2>
                      </div>
                    </div>
                  )}
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                  {collection.products.map((product, productIndex) => (
                    <motion.article
                      key={product.id}
                      className="group"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: productIndex * 0.05 }}
                      data-testid={`collection-product-${product.slug}`}
                    >
                      <Link to={`/products/${product.slug}`}>
                        <div className="relative aspect-[4/5] overflow-hidden bg-[#121212]">
                          {product.cover_image ? (
                            <img 
                              src={resolveImageUrl(product.cover_image)} 
                              alt={product.name}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-600">
                              No Image
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                        </div>
                        <div className="mt-6">
                          <h3 className="font-['Playfair_Display'] text-xl text-white group-hover:text-gray-300 transition-colors">
                            {product.name}
                          </h3>
                          <p className="text-gray-500 text-sm mt-2 line-clamp-2">
                            {product.description}
                          </p>
                        </div>
                      </Link>
                      <button
                        onClick={() => openModal(product)}
                        className="mt-4 inline-flex items-center text-xs tracking-widest uppercase text-gray-400 hover:text-[#C5A059] transition-colors"
                        data-testid={`request-btn-${product.slug}`}
                      >
                        Submit a Request
                      </button>
                    </motion.article>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="py-16 px-6 sm:px-12 lg:px-24 bg-[#0A0A0A] text-center">
        <Link 
          to="/collections"
          className="inline-flex items-center gap-3 border border-white/40 rounded-full text-white px-8 py-4 text-sm tracking-widest uppercase font-medium hover:border-white hover:bg-white hover:text-black transition-colors duration-300"
        >
          View All Collections
          <ArrowRight size={16} strokeWidth={1.5} />
        </Link>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 sm:px-12 lg:px-24 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <h3 className="font-['Playfair_Display'] text-2xl text-white mb-4">INTROZZI</h3>
              <p className="text-gray-500 text-sm font-light max-w-sm">
                Tailored bathroom fixtures and accessories. Crafted with precision, designed for silence.
              </p>
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">Navigate</p>
              <ul className="space-y-3">
                <li><Link to="/collections" className="text-gray-400 hover:text-white transition-colors text-sm">Collections</Link></li>
                <li><Link to="/#our-story" className="text-gray-400 hover:text-white transition-colors text-sm">Our Story</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">Contact</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">Contact</p>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li>contact@introzzi.ch</li>
              </ul>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-xs">&copy; 2022 INTROZZI. All rights reserved.</p>
            <p className="text-gray-700 text-[10px] mt-1">brand of idrotek &mdash; CHE-468.961.634</p>
            <div className="flex items-center gap-4">
              <span className="w-12 h-px bg-[#C5A059]" />
              <Link 
                to="/admin/login" 
                className="text-gray-700 hover:text-gray-500 transition-colors text-[10px]"
                data-testid="hidden-admin-link"
              >
                ●
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CollectionsPage;
